import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { getInitialFilter, setUrlFilter, type HandleFilter } from '../App';
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useNodesInitialized,
  Handle,
  Position,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from '@dagrejs/dagre';
import { CT } from '../lib/theme';

const COLORS: Record<string, { bg: string; border: string }> = {
  persona: { bg: CT.darkBg, border: CT.orange },
  situation: { bg: CT.darkBg, border: CT.purple },
  pain: { bg: CT.darkBg, border: CT.blue },
  solution: { bg: CT.darkBg, border: CT.green },
};

const HANDLE_SIZE = 7;
const HANDLE_SIZE_ACTIVE = 12;

function PSPSNode({
  id,
  data,
}: {
  id: string;
  data: {
    type: string;
    label: string;
    description?: string;
    _expanded?: boolean;
    _targetHandleActive?: boolean;
    _sourceHandleActive?: boolean;
    _deadEnd?: boolean;
    onHandleClick?: (nodeId: string, handleType: string) => void;
  };
}) {
  const c = COLORS[data.type] ?? { bg: CT.darkBg, border: CT.lightGrey };
  const expanded = !!data._expanded;
  const hasDesc = data.description && data.description.length > 0;
  const targetActive = !!data._targetHandleActive;
  const sourceActive = !!data._sourceHandleActive;
  const deadEnd = !!data._deadEnd;
  const onHandleClick = data.onHandleClick;

  const handleStyle = (active: boolean) => ({
    background: c.border,
    border: 'none',
    width: active ? HANDLE_SIZE_ACTIVE : HANDLE_SIZE,
    height: active ? HANDLE_SIZE_ACTIVE : HANDLE_SIZE,
    transition: 'width 0.2s ease, height 0.2s ease',
  });

  const wrapHandle = (type: 'target' | 'source', active: boolean) => (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onHandleClick?.(id, type);
      }}
      style={{
        position: 'absolute',
        top: '50%',
        [type === 'target' ? 'left' : 'right']: 0,
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: active ? HANDLE_SIZE_ACTIVE + 8 : HANDLE_SIZE + 8,
        height: active ? HANDLE_SIZE_ACTIVE + 8 : HANDLE_SIZE + 8,
        marginLeft: type === 'target' ? -4 : 0,
        marginRight: type === 'source' ? -4 : 0,
      }}
    >
      <Handle
        type={type}
        position={type === 'target' ? Position.Left : Position.Right}
        style={handleStyle(active)}
      />
    </div>
  );

  return (
    <div
      style={{
        background: c.bg,
        border: `1.5px solid ${c.border}`,
        borderRadius: 8,
        padding: '8px 12px',
        width: expanded ? 'auto' : 160,
        minWidth: 160,
        maxWidth: expanded ? 320 : 160,
        cursor: hasDesc ? 'pointer' : 'default',
        fontFamily: CT.font,
        letterSpacing: '0em',
        position: 'relative',
        transition: 'opacity 0.25s ease',
      }}
    >
      {data.type !== 'persona' && wrapHandle('target', targetActive)}
      {data.type !== 'solution' && !deadEnd && wrapHandle('source', sourceActive)}

      <div
        style={{
          color: CT.white,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.3,
          letterSpacing: '0.01em',
          wordBreak: 'break-word',
          marginBottom: expanded && hasDesc ? 8 : 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
        }}
      >
        <span style={{ flex: 1 }}>{data.label}</span>
        {deadEnd && (
          <span
            title="Needs further research"
            style={{
              flexShrink: 0,
              fontSize: 9,
              lineHeight: '16px',
              padding: '0 5px',
              borderRadius: 4,
              background: `${CT.orange}20`,
              color: CT.orange,
              fontWeight: 600,
              letterSpacing: '0.03em',
              whiteSpace: 'nowrap',
            }}
          >
            ✧ TODO
          </span>
        )}
      </div>

      {expanded && hasDesc && (
        <div
          style={{
            color: CT.paleGrey,
            fontSize: 10.5,
            fontWeight: 200,
            lineHeight: 1.4,
            letterSpacing: '0.01em',
            borderTop: `1px solid ${c.border}30`,
            paddingTop: 6,
            marginTop: 2,
          }}
        >
          {data.description}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { psps: PSPSNode };

function edgeColorForTarget(targetId: string, nodeMap: Record<string, Node>) {
  const node = nodeMap[targetId];
  if (!node) return CT.lightGrey;
  return COLORS[node.data?.type as string]?.border ?? CT.lightGrey;
}

function buildEdge(
  src: string,
  tgt: string,
  color: string
): Edge {
  return {
    id: `${src}-${tgt}`,
    source: src,
    target: tgt,
    type: 'smoothstep',
    style: { stroke: color, strokeWidth: 1.5, opacity: 0.5 },
    animated: false,
  };
}

function collectDownstream(
  edgeId: string,
  edgeList: Edge[],
  edgeIndex: Record<string, Edge>
) {
  const edge = edgeIndex[edgeId];
  if (!edge) return { nodeIds: new Set<string>(), edgeIds: new Set<string>() };

  const nodeIds = new Set<string>([edge.source, edge.target]);
  const edgeIds = new Set<string>([edgeId]);
  const queue = [edge.target];

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const e of edgeList) {
      if (e.source === current && !edgeIds.has(e.id)) {
        edgeIds.add(e.id);
        nodeIds.add(e.target);
        queue.push(e.target);
      }
    }
  }

  const upQueue = [edge.source];
  while (upQueue.length > 0) {
    const current = upQueue.shift()!;
    for (const e of edgeList) {
      if (e.target === current && !edgeIds.has(e.id)) {
        edgeIds.add(e.id);
        nodeIds.add(e.source);
        upQueue.push(e.source);
      }
    }
  }

  return { nodeIds, edgeIds };
}

const DEFAULT_W = 160;
const DEFAULT_H = 52;

function dagreLayout(rawNodes: Node[], rawEdges: Array<[string, string]>, measured?: Record<string, { w: number; h: number }>): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 30, ranksep: 80, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  const filtered = rawNodes.filter((n) => n.data?.type !== 'project');
  for (const n of filtered) {
    const m = measured?.[n.id];
    g.setNode(n.id, { width: m?.w ?? DEFAULT_W, height: m?.h ?? DEFAULT_H });
  }

  const nodeIdSet = new Set(filtered.map((n) => n.id));
  for (const [src, tgt] of rawEdges) {
    if (nodeIdSet.has(src) && nodeIdSet.has(tgt)) g.setEdge(src, tgt);
  }

  dagre.layout(g);

  return filtered.map((n) => {
    const gn = g.node(n.id);
    const m = measured?.[n.id];
    return {
      ...n,
      position: { x: gn.x - gn.width / 2, y: gn.y - gn.height / 2 },
      ...(m ? { width: m.w, height: m.h } : {}),
    };
  });
}

export type PSPSProjectData = {
  nodes: Array<{
    id: string;
    type: string;
    data: { type: string; id: number; label: string; description: string };
  }>;
  edges: Array<[string, string]>;
  minimal?: boolean;
};

const EMPTY_NODES: PSPSProjectData['nodes'] = [];
const EMPTY_EDGES: PSPSProjectData['edges'] = [];

/** Returns true if the node was found and focused, false if not on canvas. */
export type FocusNodeFn = (nodeId: string) => boolean;

function PSPSProjectInner({ projectId, data, onFocusNodeRef }: { projectId: number | null; data: PSPSProjectData | null; onFocusNodeRef?: React.MutableRefObject<FocusNodeFn | null> }) {
  const minimal = !!(data?.minimal);

  const rawNodes = data?.nodes ?? EMPTY_NODES;
  const edgePairs = data?.edges ?? EMPTY_EDGES;

  const nodeMap = useMemo(
    () => Object.fromEntries(rawNodes.map((n) => [n.id, n])),
    [rawNodes]
  );

  const baseEdges = useMemo(() => {
    if (edgePairs.length === 0) return [];
    return edgePairs
      .filter(([src]) => nodeMap[src]?.data?.type !== 'project')
      .map(([src, tgt]) => buildEdge(src, tgt, edgeColorForTarget(tgt, nodeMap as Record<string, Node>)));
  }, [edgePairs, nodeMap]);

  const edgeIndex = useMemo(
    () => Object.fromEntries(baseEdges.map((e) => [e.id, e])),
    [baseEdges]
  );

  const initialLayout = useMemo(() => dagreLayout(rawNodes as Node[], edgePairs), [rawNodes, edgePairs]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [, , onEdgesChange] = useEdgesState([]);
  const nodesInitialized = useNodesInitialized();
  const didMeasureRef = useRef(false);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const [handleFilters, setHandleFilters] = useState<HandleFilter[]>(getInitialFilter);

  // Resolve handle filters to edge IDs
  const selectedEdgeIds = useMemo(() => {
    const set = new Set<string>();
    for (const f of handleFilters) {
      for (const e of baseEdges) {
        if (f.direction === 'out' && e.source === f.nodeId) set.add(e.id);
        if (f.direction === 'in' && e.target === f.nodeId) set.add(e.id);
      }
    }
    return set;
  }, [handleFilters, baseEdges]);

  // Sync filter to URL hash
  useEffect(() => {
    setUrlFilter(handleFilters);
  }, [handleFilters]);

  // Pass 1: initial layout with estimated dimensions
  useEffect(() => {
    didMeasureRef.current = false;
    setNodes(initialLayout);
  }, [initialLayout, setNodes]);

  // Pass 2: re-layout with actual measured dimensions
  useEffect(() => {
    if (!nodesInitialized || didMeasureRef.current) return;
    didMeasureRef.current = true;
    const measured: Record<string, { w: number; h: number }> = {};
    let hasMeasured = false;
    for (const n of nodesRef.current) {
      if (n.width && n.height) {
        measured[n.id] = { w: n.width, h: n.height };
        hasMeasured = true;
      }
    }
    if (!hasMeasured) return;
    setNodes(dagreLayout(rawNodes as Node[], edgePairs, measured));
  }, [nodesInitialized, rawNodes, edgePairs, setNodes]);

  // Reset filter on project change, but skip the initial mount (preserves URL filter)
  const prevProjectId = useRef(projectId);
  useEffect(() => {
    if (prevProjectId.current !== projectId) {
      prevProjectId.current = projectId;
      setHandleFilters([]);
    }
  }, [projectId]);

  const filterResult = useMemo(() => {
    if (selectedEdgeIds.size === 0) return null;
    const combinedNodes = new Set<string>();
    const combinedEdges = new Set<string>();
    for (const eid of selectedEdgeIds) {
      const { nodeIds, edgeIds } = collectDownstream(eid, baseEdges, edgeIndex);
      nodeIds.forEach((id) => combinedNodes.add(id));
      edgeIds.forEach((id) => combinedEdges.add(id));
    }
    return { nodeIds: combinedNodes, edgeIds: combinedEdges };
  }, [selectedEdgeIds, baseEdges, edgeIndex]);

  const hasFilter = filterResult !== null;

  const onHandleClick = useCallback(
    (nodeId: string, handleType: string) => {
      const direction: 'in' | 'out' = handleType === 'target' ? 'in' : 'out';
      setHandleFilters((prev) => {
        const alreadySet =
          prev.length === 1 &&
          prev[0].nodeId === nodeId &&
          prev[0].direction === direction;
        return alreadySet ? [] : [{ nodeId, direction }];
      });
    },
    []
  );

  const nodesWithOutgoing = useMemo(() => {
    const set = new Set<string>();
    for (const e of baseEdges) set.add(e.source);
    return set;
  }, [baseEdges]);

  const styledNodes = useMemo(() => {
    let base: Node[];
    if (!hasFilter) {
      base = nodes;
    } else {
      const measured: Record<string, { w: number; h: number }> = {};
      const expandedIds = new Set<string>();
      for (const n of nodes) {
        if (n.width && n.height) measured[n.id] = { w: n.width, h: n.height };
        if (n.data?._expanded) expandedIds.add(n.id);
      }
      base = dagreLayout(
        (rawNodes as Node[]).filter((n) => filterResult!.nodeIds.has(n.id)),
        edgePairs.filter(([src, tgt]) => filterResult!.nodeIds.has(src) && filterResult!.nodeIds.has(tgt)),
        measured
      ).map((n) => expandedIds.has(n.id) ? { ...n, data: { ...n.data, _expanded: true } } : n);
    }
    return base.map((n) => {
      const targetActive =
        hasFilter &&
        baseEdges.some((e) => e.target === n.id && selectedEdgeIds.has(e.id));
      const sourceActive =
        hasFilter &&
        baseEdges.some((e) => e.source === n.id && selectedEdgeIds.has(e.id));
      const nodeType = n.data?.type as string;
      const isDeadEnd =
        (nodeType === 'persona' || nodeType === 'situation' || nodeType === 'pain') &&
        !nodesWithOutgoing.has(n.id);
      return {
        ...n,
        zIndex: n.data?._expanded ? 1000 : 0,
        data: {
          ...n.data,
          _targetHandleActive: targetActive,
          _sourceHandleActive: sourceActive,
          _deadEnd: isDeadEnd,
          onHandleClick,
        },
      };
    });
  }, [nodes, hasFilter, filterResult, baseEdges, selectedEdgeIds, onHandleClick, nodesWithOutgoing, rawNodes, edgePairs]);

  const styledEdges = useMemo(() => {
    const base = !hasFilter
      ? baseEdges.map((e) => ({ ...e, style: { ...e.style, strokeWidth: 1.5, opacity: 0.5 } }))
      : baseEdges
          .filter((e) => filterResult!.edgeIds.has(e.id))
          .map((e) => ({
            ...e,
            style: {
              stroke: e.style?.stroke,
              strokeWidth: selectedEdgeIds.has(e.id) ? 3.5 : 1.5,
              opacity: selectedEdgeIds.has(e.id) ? 0.95 : 0.5,
            },
          }));
    return base.map((e) => ({ ...e, style: { ...e.style, pointerEvents: 'none' as const } }));
  }, [baseEdges, hasFilter, filterResult, selectedEdgeIds]);

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!node.data?.description) return;
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, _expanded: !n.data?._expanded } }
            : n
        )
      );
    },
    [setNodes]
  );

  // Expose focusNode via ref for external callers (e.g. sidebar changelog)
  const rfInstanceRef = useRef<{ getNode: (id: string) => Node | undefined; setCenter: (x: number, y: number, opts?: { zoom?: number; duration?: number }) => void } | null>(null);

  useEffect(() => {
    if (!onFocusNodeRef) return;
    onFocusNodeRef.current = (nodeId: string): boolean => {
      const rf = rfInstanceRef.current;
      if (!rf) return false;

      // Check if node exists before clearing filters
      const exists = !!rf.getNode(nodeId);
      if (!exists) return false;

      // Clear any active handle filters that might hide the node
      setHandleFilters([]);
      setUrlFilter([]);

      // Small delay to let the filter reset re-render nodes before we look them up
      setTimeout(() => {
        const node = rf.getNode(nodeId);
        if (!node) return;
        const x = (node.position?.x ?? 0) + ((node.width ?? 200) / 2);
        const y = (node.position?.y ?? 0) + ((node.height ?? 60) / 2);
        rf.setCenter(x, y, { zoom: 1.4, duration: 500 });

        // Also expand the node
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, _expanded: true } }
              : n
          )
        );
      }, 50);
      return true;
    };
  }, [onFocusNodeRef, setNodes, setHandleFilters]);

  return (
    <div
      style={{
        width: '100%',
        height: minimal ? 800 : '100%',
        background: CT.darkGrey,
        fontFamily: CT.font,
      }}
    >
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onInit={(instance) => { rfInstanceRef.current = instance as typeof rfInstanceRef.current; }}
        nodeTypes={nodeTypes}
        elementsSelectable={false}
        nodesConnectable={false}
        edgesUpdatable={false}
        connectOnClick={false}
        defaultViewport={{ x: 20, y: 50, zoom: 1 }}
        minZoom={0.2}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
      />
    </div>
  );
}

export default function PSPSProject(props: { projectId: number | null; data: PSPSProjectData | null; onFocusNodeRef?: React.MutableRefObject<FocusNodeFn | null> }) {
  return (
    <ReactFlowProvider>
      <PSPSProjectInner {...props} />
    </ReactFlowProvider>
  );
}
